import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { LoadingButton } from '@mui/lab';
import { PATH_AUTH } from '../../../routes/paths';
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField } from '../../../components/hook-form';

// takes out the autofill color
const textFieldStyles = {
  '& .MuiOutlinedInput-root': { 
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'primary.main'
    },
    '& input:-webkit-autofill': {
      '-webkit-box-shadow': '0 0 0px 1000px white inset',
      'transition': 'background-color 5000s ease-in-out 0s'
    }
  }
};

export default function LoginForm() {
  const { login } = useAuth();
  const navigate = useNavigate();
  
  const isMountedRef = useIsMountedRef();

  const [showPassword, setShowPassword] = useState(false);
  // State for error message from sessionStorage
  const [sessionErrorMessage, setSessionErrorMessage] = useState('');
  
  // Check for error message in sessionStorage on component mount
  useEffect(() => {
    const errorMessage = sessionStorage.getItem('loginErrorMessage');
    if (errorMessage) {
      setSessionErrorMessage(errorMessage);
      // Clear the message from sessionStorage
      sessionStorage.removeItem('loginErrorMessage');
    }
  }, []);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string().required('Password is required'),
  });

  const defaultValues = {
    email: '',
    password: '',

    // email: 'mike.krog+demo10@levar.io',
    // password: 'Winter@123'
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    try {
      // Clear any session error message
      setSessionErrorMessage('');
      
      const response = await login(data.email, data.password);
      
      // Check if the user needs to change their password (admin-created user)
      if (response && response.requiresNewPassword) {
        console.log('Redirecting to new password page');
        // Store the email, session token and timestamp in sessionStorage for the new password page
        sessionStorage.setItem('newPasswordEmail', data.email);
        sessionStorage.setItem('newPasswordSession', response.sessionToken);
        sessionStorage.setItem('newPasswordTimestamp', Date.now().toString());
        
        // Also store the password - we may need it for session recovery
        sessionStorage.setItem('newPasswordCurrentPassword', data.password);
        
        // Redirect to the new password page
        navigate(PATH_AUTH.newPassword);
        return;
      }
    } catch (error) {
      console.error("error catcher in login form", error);

      reset();

      if (isMountedRef.current) {
        setError('afterSubmit', { ...error, message: error.message });
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}
        
        {/* Display error message from session storage if it exists */}
        {!!sessionErrorMessage && (
          <Alert severity="error">{sessionErrorMessage}</Alert>
        )}

        <RHFTextField 
          name="email" 
          label="Email address" 
          sx={textFieldStyles}
        />

        <RHFTextField
          name="password"
          label="Password"
          autoComplete="current-password"
          type={showPassword ? 'text' : 'password'}
          sx={textFieldStyles}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ my: 2 }}>
        <Link component={RouterLink} variant="subtitle2" to={PATH_AUTH.forgotPassword}>
          Forgot password?
        </Link>
      </Stack>

      <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
        Login
      </LoadingButton>
    </FormProvider>
  );
}
